import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FileText, Send, Sparkles, TrendingUp, DollarSign, BarChart3,
  Loader2, Copy, Download, RotateCcw, Clock, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useToast } from "@/hooks/use-toast";
import { computeFinancialMetrics } from "@/lib/accounting/financialEngine";

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const REPORT_TEMPLATES = [
  { icon: BarChart3, label: "Financial Summary", prompt: "Generate a complete financial summary for last quarter including revenue, expenses, profit and key insights" },
  { icon: TrendingUp, label: "Revenue Analysis", prompt: "Analyze revenue trends, top income sources, and growth patterns for the current period" },
  { icon: DollarSign, label: "Expense Report", prompt: "Generate a detailed expense report with category-wise breakdown and optimization suggestions" },
  { icon: FileText, label: "P&L Statement", prompt: "Create a Profit & Loss statement with comparisons and margin analysis" },
];

function ReportRenderer({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="space-y-1.5 text-sm leading-relaxed report-content">
      {lines.map((line, i) => {
        if (line.startsWith("| ") && line.includes("|")) {
          const cells = line.split("|").filter(Boolean).map((c) => c.trim());
          if (cells.every((c) => /^[-:]+$/.test(c))) return null;
          const isHeader = i < lines.length - 1 && lines[i + 1]?.includes("---");
          return (
            <div key={i} className={`grid gap-2 px-3 py-2 rounded text-xs ${isHeader ? "bg-muted/60 font-semibold text-foreground" : "border-b border-border/20"}`}
              style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
              {cells.map((c, ci) => (
                <span key={ci} className={ci > 0 ? "text-right" : ""}>{c.replace(/\*\*/g, "")}</span>
              ))}
            </div>
          );
        }
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return (
            <div key={i} className="flex gap-2 text-xs pl-3">
              <span className="text-accent mt-0.5">•</span>
              <span className="text-muted-foreground" dangerouslySetInnerHTML={{
                __html: line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
              }} />
            </div>
          );
        }
        if (line.startsWith("### ")) {
          return <h3 key={i} className="text-sm font-semibold text-foreground mt-3 mb-1">{line.slice(4).replace(/\*\*/g, "")}</h3>;
        }
        if (line.startsWith("## ")) {
          return <h2 key={i} className="text-base font-bold text-foreground mt-4 mb-1.5 border-b border-border/30 pb-1">{line.slice(3).replace(/\*\*/g, "")}</h2>;
        }
        if (line.startsWith("# ")) {
          return <h1 key={i} className="text-lg font-bold text-foreground mt-4 mb-2">{line.slice(2).replace(/\*\*/g, "")}</h1>;
        }
        if (!line.trim()) return <div key={i} className="h-1.5" />;
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>');
        return <p key={i} className="text-muted-foreground text-xs" dangerouslySetInnerHTML={{ __html: formatted }} />;
      })}
    </div>
  );
}

interface GeneratedReport {
  id: string;
  prompt: string;
  content: string;
  timestamp: string;
  isAI: boolean;
}

const AIReportGeneratorPage = () => {
  const { vouchers, ledgers } = useAccounting();
  const { expenses } = useExpenses();
  const { activeCompany } = useCompany();
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentReport, setCurrentReport] = useState<GeneratedReport | null>(null);
  const [streamContent, setStreamContent] = useState("");
  const [history, setHistory] = useState<GeneratedReport[]>([]);
  const reportRef = useRef<HTMLDivElement>(null);

  const approved = vouchers.filter((v) => v.status === "Approved");
  const metrics = computeFinancialMetrics({ vouchers, ledgers, expenses });

  useEffect(() => {
    if (reportRef.current) {
      reportRef.current.scrollTop = reportRef.current.scrollHeight;
    }
  }, [streamContent]);

  const buildFinancialData = useCallback(() => {
    const salesVouchers = approved.filter((v) => v.type === "Sales");
    const purchaseVouchers = approved.filter((v) => v.type === "Purchase");
    const receiptVouchers = approved.filter((v) => v.type === "Receipt");
    const paymentVouchers = approved.filter((v) => v.type === "Payment");

    return {
      summary: {
        totalRevenue: metrics.netSalesRevenue,
        grossSales: metrics.grossSales,
        totalPurchases: metrics.taxablePurchases,
        grossPurchases: metrics.grossPurchases,
        totalExpenses: metrics.operatingExpenses,
        cogs: metrics.cogs,
        grossProfit: metrics.grossProfit,
        grossProfitMargin: `${metrics.grossProfitMargin}%`,
        netProfit: metrics.netProfit,
        profitMargin: metrics.netSalesRevenue > 0 ? `${metrics.netProfitMargin}%` : "N/A",
        expenseRatio: `${metrics.operatingExpenseRatio}%`,
        gstStatusLabel: metrics.gstStatusLabel,
        gstStatusAmount: metrics.netGstPayable > 0 ? metrics.netGstPayable : metrics.excessITC,
        totalVouchers: approved.length,
        pendingVouchers: vouchers.filter((v) => v.status === "Pending").length,
      },
      sales: {
        count: salesVouchers.length,
        total: salesVouchers.reduce((s, v) => s + v.totalDebit, 0),
        taxable: metrics.netSalesRevenue,
        gstCollected: metrics.gstOutput,
        topParties: salesVouchers.filter((v) => v.partyName).slice(0, 5).map((v) => ({
          name: v.partyName, amount: v.totalDebit, invoice: v.invoiceNumber
        })),
      },
      purchases: {
        count: purchaseVouchers.length,
        total: purchaseVouchers.reduce((s, v) => s + v.totalDebit, 0),
        taxable: metrics.taxablePurchases,
        gstPaid: metrics.gstInput,
        topVendors: purchaseVouchers.filter((v) => v.partyName).slice(0, 5).map((v) => ({
          name: v.partyName, amount: v.totalDebit
        })),
      },
      receipts: { count: receiptVouchers.length, total: receiptVouchers.reduce((s, v) => s + v.totalDebit, 0) },
      payments: { count: paymentVouchers.length, total: paymentVouchers.reduce((s, v) => s + v.totalDebit, 0) },
      expenses: expenses.slice(0, 20).map((e) => ({
        amount: e.amount, category: e.category, vendor: e.vendor, date: e.date, status: e.status
      })),
      ledgerBalances: ledgers.slice(0, 20).map((l) => ({
        name: l.name, group: l.group, balance: l.currentBalance, balanceType: l.currentBalanceType
      })),
    };
  }, [approved, vouchers, ledgers, expenses, metrics]);

  const generateReport = useCallback(async (text: string) => {
    if (!text.trim() || isGenerating) return;
    setIsGenerating(true);
    setStreamContent("");
    setCurrentReport(null);

    const financialData = buildFinancialData();

    try {
      const resp = await fetch("/api/ai/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: text, companyId: activeCompany?.id }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "AI service error" }));
        throw new Error(err.error || `Error ${resp.status}`);
      }

      const resData = await resp.json();
      const fullContent = resData.content || resData.data?.content || "";

      const report: GeneratedReport = {
        id: crypto.randomUUID(),
        prompt: text,
        content: fullContent || generateLocalReport(text, financialData),
        timestamp: new Date().toISOString(),
        isAI: !!fullContent,
      };
      setCurrentReport(report);
      setHistory((prev) => [report, ...prev].slice(0, 10));
      setStreamContent("");
    } catch (err: any) {
      console.error("Report generation error:", err);
      // Fallback to local generation
      const financialData = buildFinancialData();
      const localContent = generateLocalReport(text, financialData);
      const report: GeneratedReport = {
        id: crypto.randomUUID(),
        prompt: text,
        content: localContent,
        timestamp: new Date().toISOString(),
        isAI: false,
      };
      setCurrentReport(report);
      setHistory((prev) => [report, ...prev].slice(0, 10));
      setStreamContent("");
      toast({ title: "Report generated", description: err.message, variant: "default" });
    } finally {
      setIsGenerating(false);
      setPrompt("");
    }
  }, [isGenerating, buildFinancialData, activeCompany, toast]);

  const copyReport = () => {
    const content = currentReport?.content || streamContent;
    if (content) {
      navigator.clipboard.writeText(content);
      toast({ title: "Copied to clipboard" });
    }
  };

  const downloadReport = () => {
    const content = currentReport?.content || streamContent;
    if (content) {
      const blob = new Blob([content], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financial-report-${new Date().toISOString().split("T")[0]}.md`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); generateReport(prompt); }
  };

  const displayContent = streamContent || currentReport?.content || "";

  return (
    <div className="space-y-4 h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-accent" />
            AI Report Generator
          </h1>
          <p className="text-sm text-muted-foreground">Generate intelligent financial reports from natural language</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-2 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> AI Powered
        </span>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Revenue", value: fmt(metrics.netSalesRevenue), icon: TrendingUp, color: "text-success" },
          { label: "Expenses", value: fmt(metrics.operatingExpenses), icon: DollarSign, color: "text-destructive" },
          { label: "Net Profit", value: fmt(metrics.netProfit), icon: BarChart3, color: metrics.netProfit >= 0 ? "text-success" : "text-destructive" },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-muted/50 flex items-center justify-center`}>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
                <p className="text-sm font-bold text-foreground">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 flex-1" style={{ height: "calc(100% - 180px)" }}>
        {/* Left: Input + Templates */}
        <div className="lg:col-span-1 space-y-3">
          <Card className="bg-card border-border">
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-xs font-semibold text-foreground">Quick Templates</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 space-y-1.5">
              {REPORT_TEMPLATES.map((t) => (
                <motion.button
                  key={t.label}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => generateReport(t.prompt)}
                  disabled={isGenerating}
                  className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-border hover:border-accent/30 hover:bg-accent/5 transition-all text-left disabled:opacity-50"
                >
                  <t.icon className="h-3.5 w-3.5 text-accent flex-shrink-0" />
                  <span className="text-xs text-foreground">{t.label}</span>
                </motion.button>
              ))}
            </CardContent>
          </Card>

          {history.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> Recent Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1">
                {history.slice(0, 5).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setCurrentReport(r); setStreamContent(""); }}
                    className="w-full text-left p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <p className="text-[11px] text-foreground truncate">{r.prompt}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      {r.isAI ? <Sparkles className="h-2.5 w-2.5 text-accent" /> : <CheckCircle2 className="h-2.5 w-2.5" />}
                      {new Date(r.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Report Display */}
        <div className="lg:col-span-3 flex flex-col">
          {displayContent ? (
            <Card className="bg-card border-border flex-1 flex flex-col overflow-hidden">
              <CardHeader className="p-3 pb-2 flex flex-row items-center justify-between border-b border-border/30">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xs font-semibold text-foreground">Generated Report</CardTitle>
                  {currentReport?.isAI && (
                    <span className="text-[9px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">AI Generated</span>
                  )}
                  {isGenerating && (
                    <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" /> Generating...
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={copyReport}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={downloadReport}>
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                </div>
              </CardHeader>
              <CardContent ref={reportRef} className="p-4 flex-1 overflow-y-auto">
                <ReportRenderer content={displayContent} />
                {isGenerating && (
                  <div className="flex items-center gap-2 mt-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    <span className="text-[10px] text-muted-foreground">AI is writing...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border flex-1 flex items-center justify-center">
              <div className="text-center p-8">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-7 w-7 text-accent" />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">Generate a Financial Report</h3>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Type a request like "Generate financial summary for last quarter" or pick a template
                </p>
              </div>
            </Card>
          )}

          {/* Input */}
          <div className="mt-3">
            <div className="flex gap-2">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Generate financial summary for last quarter..."
                className="min-h-[44px] max-h-[100px] resize-none"
                rows={1}
                disabled={isGenerating}
              />
              <Button onClick={() => generateReport(prompt)} disabled={!prompt.trim() || isGenerating} size="icon" className="h-[44px] w-[44px] flex-shrink-0">
                {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">AI analyzes your real accounting data to generate reports. Press Enter to send.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Local fallback report generator
function generateLocalReport(prompt: string, data: any): string {
  const s = data.summary;
  const fmtN = (n: number) => `₹${n.toLocaleString("en-IN")}`;

  if (s.totalVouchers === 0) {
    return `# Financial Analysis Report

## Status: Empty Business Workspace

### Executive Summary
There are currently no recorded accounting transactions or approved vouchers in this business workspace.

### Next Steps to Generate Financial Reports
1. Record your sales invoices in the **Sales** module.
2. Enter vendor bills and operational costs in **Purchases** and **Expenses**.
3. Reconcile bank balances and configure ledgers.

---
*Report generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}*`;
  }

  return `# Financial Report

## Executive Summary

This report provides an authoritative overview of your company's financial position based on ${s.totalVouchers} approved transactions. Net sales revenue stands at ${fmtN(s.totalRevenue)} (gross invoiced: ${fmtN(s.grossSales || s.totalRevenue)}) with operating expenses at ${fmtN(s.totalExpenses)}, resulting in a net profit of ${fmtN(s.netProfit)}.

## Revenue Analysis

| Metric | Value |
|--------|-------|
| Net Sales Revenue (Taxable) | ${fmtN(s.totalRevenue)} |
| Gross Invoiced Sales | ${fmtN(s.grossSales || s.totalRevenue)} |
| Sales Transactions | ${data.sales.count} |
| Output GST Collected | ${fmtN(data.sales.gstCollected)} |
| Avg. Sale Value | ${data.sales.count > 0 ? fmtN(data.sales.total / data.sales.count) : "N/A"} |

${data.sales.topParties.length > 0 ? `### Top Customers\n${data.sales.topParties.map((p: any) => `- **${p.name}** — ${fmtN(p.amount)}${p.invoice ? ` (${p.invoice})` : ""}`).join("\n")}` : ""}

## Expense & Cost Analysis

| Metric | Value |
|--------|-------|
| Cost of Goods Sold (COGS) | ${fmtN(s.cogs || 0)} |
| Operating Overheads | ${fmtN(s.totalExpenses)} |
| Operating Expense Ratio | ${s.expenseRatio} |
| Purchases Capitalized to Inventory | ${fmtN(s.totalPurchases || 0)} |
| Eligible Input Tax Credit (ITC) | ${fmtN(data.purchases.gstPaid)} |
| Supplier Payments Settled | ${data.payments.count} (${fmtN(data.payments.total)}) |

${data.purchases.topVendors.length > 0 ? `### Top Vendors\n${data.purchases.topVendors.map((v: any) => `- **${v.name}** — ${fmtN(v.amount)}`).join("\n")}` : ""}

## Profit & Loss Summary

| Item | Amount |
|------|--------|
| Net Sales Revenue | ${fmtN(s.totalRevenue)} |
| Less: Cost of Goods Sold (COGS) | (${fmtN(s.cogs || 0)}) |
| **Gross Profit** | **${fmtN(s.grossProfit || (s.totalRevenue - (s.cogs || 0)))}** |
| Gross Margin | ${s.grossProfitMargin || "N/A"} |
| Less: Operating Expenses | (${fmtN(s.totalExpenses)}) |
| **Net Profit** | **${fmtN(s.netProfit)}** |
| Net Profit Margin | ${s.profitMargin} |

## GST & Cash Position

- **GST Status:** ${s.gstStatusLabel}: **${fmtN(s.gstStatusAmount || 0)}** (Output Tax: ${fmtN(data.sales.gstCollected)} minus Input Tax Credit: ${fmtN(data.purchases.gstPaid)})
- **Cash Position:** ${fmtN(data.receipts.total)} received vs ${fmtN(data.payments.total)} paid out
- **Operating Overheads:** ${data.expenses.length > 0 ? `${data.expenses.length} expense entries recorded in operational categories totaling ${fmtN(s.totalExpenses)}` : "No direct overheads recorded"}

## Recommendations

1. ${s.pendingVouchers > 0 ? "Approve pending vouchers to ensure real-time financial reporting" : "Maintain current real-time voucher approval workflows"}
2. ${data.purchases.gstPaid > 0 ? "Ensure all purchase invoices match GSTR-2B before filing monthly returns" : "Capture GSTIN on all vendor bills to maximize eligible input tax credits"}
3. Maintain current healthy cost discipline and inventory turnover.

---
*Report generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}*
`;
}

export default AIReportGeneratorPage;
